import React from "react";
import { useNavigate } from "react-router-dom";
import Typography from "@dtci/spellbook/Typography";
import Button from "@dtci/spellbook/Button";
import { useJobsActions } from "tap/store/licensingJobs/useJobs.js";
import { updateSearchParams } from "tap/utils/urlParams.js";
import "./styles.scss";

/**
 * Licensing job status card on the Home dashboard.
 * @param {Object} props
 * @param {Object} props.data - Grouped licensing job lists by status
 * @param {boolean} props.isLoading - Whether job data is loading
 */
export const LicensingJobs = ({ data = {}, isLoading = false }) => {
  const navigate = useNavigate();
  const { updateJobsSearchForm } = useJobsActions();

  const {
    pending = [],
    updatedModified = [],
    updatedActive = [],
    duplicate = [],
    needsAttention = [],
    systemCreated = [],
  } = data;

  const handleViewRecord = (item) => {
    updateJobsSearchForm({
      key: "filterForm",
      data: { jobId: String(item.licensingJob.id) },
    });

    updateSearchParams({ pathname: "/licensingJobs" }, navigate, {
      jobId: String(item.licensingJob.id),
    });
  };

  const renderJobs = (jobs, emptyMessage = "None") =>
    jobs.length === 0 ? (
      <Typography
        variant="T20"
        style={{ color: "#666", fontStyle: "italic", padding: "4px 8px" }}
      >
        {emptyMessage}
      </Typography>
    ) : (
      jobs.map((item) => (
        <div
          key={item.licensingJob.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 8px",
            fontSize: "16px",
          }}
        >
          <Typography variant={"T20"}>
            {`${item.licensingJob.id} - ${item.licensingJob.description} (${item.offerCount})`}
          </Typography>
          <Button
            labelText="View"
            styleType="link"
            onClick={() => handleViewRecord(item)}
          >
            VIEW
          </Button>
        </div>
      ))
    );

  if (isLoading) {
    return (
      <div className="home-card">
        <div className="home-card-header">Status of My Licensing Job Offers</div>
        <div className="home-card-content">
          <Typography
            variant="T20"
            style={{ color: "#666", fontStyle: "italic", padding: "4px 8px" }}
          >
            Loading licensing job status...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="home-card">
      <div className="home-card-header">Status of My Licensing Job Offers</div>
      <div className="home-card-content">
        <Typography
          variant={"T40"}
          style={pending.length > 0 ? { color: "green" } : {}}
        >
          Pending
        </Typography>
        {renderJobs(pending, "No pending jobs")}
        <br />
        <Typography
          variant={"T40"}
          style={systemCreated.length > 0 ? { color: "red" } : {}}
        >
          System Created Jobs
        </Typography>
        {renderJobs(systemCreated, "No system created jobs")}
        <br />
        <Typography
          variant={"T40"}
          style={updatedModified.length > 0 ? { color: "red" } : {}}
        >
          Updated/Modified - System Updated, User Modified
        </Typography>
        {renderJobs(updatedModified, "No updated/modified jobs")}
        <br />
        <Typography
          variant={"T40"}
          style={updatedActive.length > 0 ? { color: "red" } : {}}
        >
          Active Updated/Modified
        </Typography>
        {renderJobs(updatedActive, "No active updated/modified jobs")}
        <br />
        <Typography
          variant={"T40"}
          style={duplicate.length > 0 ? { color: "red" } : {}}
        >
          Duplicate
        </Typography>
        {renderJobs(duplicate, "No duplicate jobs")}
        <br />
        <Typography
          variant={"T40"}
          style={needsAttention.length > 0 ? { color: "red" } : {}}
        >
          Needs Attention - Blank Deal Fields, Inactive Deal, Info Restrictions,
          Missing Pricing, Rights Issue, To Delete
        </Typography>
        {renderJobs(needsAttention, "No jobs needing attention")}
      </div>
    </div>
  );
};
